"use client";

import {
    FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormMessage,
} from "@/shared/components/ui/form";
import { Popover, PopoverContent, PopoverTrigger } from "@/shared/components/ui/popover";
import { Calendar } from "@/shared/components/ui/calendar";
import { Button } from "@/shared/components/ui/button";
import { CalendarIcon, X } from "lucide-react";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { cn } from "@/shared/utils/cn";
import { BaseFieldProps } from "./form-field-utils";
import { FormFieldLabel } from "./form-field-label";

export function FieldDate({
    name,
    label,
    description,
    className,
    labelType = "text",
    tooltipContent,
    placeholder = "Tarih seçin",
    disableFuture = false,
    clearable = false,
    disabled,
    controlDisabled = false,
    defaultMonth,
}: BaseFieldProps & {
    placeholder?: string;
    disableFuture?: boolean;
    clearable?: boolean;
    disabled?: (date: Date) => boolean;
    /** Alanın tamamını kapatır (takvim açılmaz) — `disabled` yalnız gün bazlı kısıttır. */
    controlDisabled?: boolean;
    /** Seçim yokken takvimin açılacağı ay — geçmiş döneme kayıt girerken cari aya düşmemek için. */
    defaultMonth?: Date;
}) {
    // Create disabled function based on disableFuture or custom disabled function
    const getDisabledDates = (date: Date) => {
        // First check custom disabled function
        if (disabled && disabled(date)) {
            return true;
        }
        // Then check disableFuture
        if (disableFuture) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const checkDate = new Date(date);
            checkDate.setHours(0, 0, 0, 0);
            return checkDate > today;
        }
        return false;
    };

    const currentYear = new Date().getFullYear();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Date range for dropdown
    const startMonth = new Date(currentYear - 100, 0);
    const endMonth = new Date(currentYear + 3, 11);

    return (
        <FormField
            name={name}
            render={({ field }) => (
                <FormItem className={cn("min-w-0", className)}>
                    <FormFieldLabel label={label} labelType={labelType} tooltipContent={tooltipContent} />
                    <div className="flex min-w-0 gap-2">
                        <Popover>
                            <PopoverTrigger asChild>
                                <FormControl>
                                    <Button
                                        type="button"
                                        ref={field.ref}
                                        variant="outline"
                                        disabled={controlDisabled}
                                        className={cn(
                                            "min-w-0 flex-1 shrink justify-start overflow-hidden text-left font-normal",
                                            !field.value && "text-muted-foreground",
                                        )}
                                    >
                                        <CalendarIcon className="h-4 w-4 shrink-0" />
                                        <span className="truncate">
                                            {field.value
                                                ? format(new Date(field.value), "d MMMM yyyy", { locale: tr })
                                                : placeholder}
                                        </span>
                                    </Button>
                                </FormControl>
                            </PopoverTrigger>
                            <PopoverContent className="w-auto p-0" align="start">
                                <Calendar
                                    mode="single"
                                    captionLayout="dropdown"
                                    selected={field.value ? new Date(field.value) : undefined}
                                    defaultMonth={field.value ? new Date(field.value) : defaultMonth}
                                    onSelect={(date) => {
                                        field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                                    }}
                                    startMonth={startMonth}
                                    endMonth={endMonth}
                                    disabled={getDisabledDates}
                                    autoFocus
                                />
                            </PopoverContent>
                        </Popover>
                        {clearable && field.value && (
                            <Button
                                type="button"
                                variant="outline"
                                size="icon"
                                aria-label="Tarihi temizle"
                                onClick={() => field.onChange("")}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                    {description && <FormDescription>{description}</FormDescription>}
                    <FormMessage />
                </FormItem>
            )}
        />
    );
}
